import type { HireReference, HireReferences } from '../operations/model';
import type { WorkdayReference } from '../profile/model';

export type PeopleRegistrationDraft = {
  legalFamilyName: string;
  legalGivenName: string;
  legalEnglishFamilyName: string;
  legalEnglishGivenName: string;
  nicknameKorean: string;
  nicknameEnglish: string;
  systemID: string;
  birthDate: string;
  homeEmail: string;
  homePhone: string;
  homeAddress: string;
  homeAddressRegion: string;
  homeAddressCity: string;
  homeAddressPostalCode: string;
  companyID: string;
  organizationID: string;
  locationID: string;
  jobProfileID: string;
  jobLevelID: string;
  workerNumber: string;
  businessTitle: string;
  jobTypeID: string;
  workSpaceID: string;
  externalEmployerName: string;
  internalSponsorWorkerID: string;
  accessReviewOn: string;
  createdPersonID: string;
  createdHireID: string;
  hireDate: string;
  gender: 'MALE' | 'FEMALE' | '';
  hireWorkerType: 'EMPLOYEE' | 'CONTINGENT_WORKER';
  hireTimeType: 'FULL_TIME' | 'PART_TIME';
  createStep: number;
  identityExpanded: boolean;
  privateExpanded: boolean;
  assignmentExpanded: boolean;
  retryOnly: boolean;
  operationKeys: { person: string; hire: string; submit: string };
  createWorkflow?: WorkdayReference;
  hireReferences: HireReferences;
  jobTypeReferences: HireReference[];
  workSpaceReferences: HireReference[];
};
const prefix = 'heyday.people.registration.v1:';
const pending = (draft: PeopleRegistrationDraft) => draft.retryOnly || Boolean(draft.createdPersonID || draft.createdHireID);
const lifetime = 8 * 60 * 60 * 1000;
const memory = new Map<string, { draft: PeopleRegistrationDraft; savedAt: number }>();
const stringFields = ["legalFamilyName", "legalGivenName", "legalEnglishFamilyName", "legalEnglishGivenName", "nicknameKorean", "nicknameEnglish", "systemID", "birthDate", "homeEmail", "homePhone", "homeAddress", "homeAddressRegion", "homeAddressCity", "homeAddressPostalCode", "companyID", "organizationID", "locationID", "jobProfileID", "jobLevelID", "workerNumber", "businessTitle", "jobTypeID", "workSpaceID", "externalEmployerName", "internalSponsorWorkerID", "accessReviewOn", "createdPersonID", "createdHireID", "hireDate"] as const;
const booleanFields = ["identityExpanded", "privateExpanded", "assignmentExpanded", "retryOnly"] as const;
const record = (value: unknown): value is Record<string, unknown> => Boolean(value && typeof value === 'object' && !Array.isArray(value));
const references = (value: unknown): boolean => Array.isArray(value) && value.length <= 10000 && value.every(item => record(item)
  && typeof item.id === 'string' && (item.descriptor === undefined || typeof item.descriptor === 'string')
  && (item.parent === undefined || (record(item.parent) && typeof item.parent.id === 'string')));
function validDraft(value: unknown): value is PeopleRegistrationDraft {
  if (!record(value)) return false;
  const keys = value.operationKeys;
  const hireReferences = value.hireReferences;
  return stringFields.every(key => typeof value[key] === 'string' && value[key].length <= 10000)
    && booleanFields.every(key => typeof value[key] === 'boolean')
    && ['', 'MALE', 'FEMALE'].includes(String(value.gender))
    && ['EMPLOYEE', 'CONTINGENT_WORKER'].includes(String(value.hireWorkerType))
    && ['FULL_TIME', 'PART_TIME'].includes(String(value.hireTimeType))
    && Number.isInteger(value.createStep) && Number(value.createStep) >= 0 && Number(value.createStep) <= 2
    && record(keys) && ['person', 'hire', 'submit'].every(key => typeof keys[key] === 'string')
    && (value.createWorkflow === undefined || (record(value.createWorkflow) && typeof value.createWorkflow.id === 'string'))
    && record(hireReferences) && ['companies', 'locations', 'jobs', 'levels', 'organizations'].every(key => references(hireReferences[key]))
    && references(value.jobTypeReferences) && references(value.workSpaceReferences);
}
export function clearPeopleRegistrationDraft(scope?: string) {
  if (scope) memory.delete(scope); else memory.clear();
  try {
    for (const key of Object.keys(window.sessionStorage)) {
      if (scope ? key === prefix + scope : key.startsWith(prefix)) window.sessionStorage.removeItem(key);
    }
  } catch { /* 저장소가 제한되어도 메모리의 개인 입력은 지웁니다. */ }
}
if (typeof window !== 'undefined') window.addEventListener('heyday:session-changed', () => clearPeopleRegistrationDraft());
export function readPeopleRegistrationDraft(scope?: string): PeopleRegistrationDraft | undefined {
  if (!scope) return undefined;
  const cached = memory.get(scope);
  if (cached && (pending(cached.draft) || Date.now() - cached.savedAt <= lifetime)) return cached.draft;
  memory.delete(scope);
  try {
    const raw = window.sessionStorage.getItem(prefix + scope);
    if (!raw) return undefined;
    if (raw.length > 2000000) { clearPeopleRegistrationDraft(scope); return undefined; }
    const value = JSON.parse(raw);
    if (!record(value) || value.version !== 1 || typeof value.savedAt !== 'number'
      || value.savedAt > Date.now() || !validDraft(value.draft) || (!pending(value.draft) && Date.now() - value.savedAt > lifetime)) {
      clearPeopleRegistrationDraft(scope); return undefined;
    }
    memory.set(scope, { draft: value.draft, savedAt: value.savedAt });
    return value.draft;
  } catch { clearPeopleRegistrationDraft(scope); return undefined; }
}
export function savePeopleRegistrationDraft(scope: string | undefined, draft: PeopleRegistrationDraft) {
  if (!scope) return;
  const savedAt = Date.now();
  memory.set(scope, { draft, savedAt });
  try {
    // 개인 입력은 계정별 이 탭 세션에만 보관하며 로그아웃/계정 전환 때 제거합니다.
    window.sessionStorage.setItem(prefix + scope, JSON.stringify({ version: 1, savedAt, draft }));
  } catch { /* 저장소 제한 시에도 메뉴 이동 중에는 현재 세션 메모리에 보관합니다. */ }
}
